import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, input, model } from '@angular/core';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { PropertyGeospatialInseeDto } from '@workspace/data-types';

@Component({
  selector: 'etxe-property-insee-card',
  standalone: true,
  imports: [CommonModule, MatExpansionModule, MatIconModule],
  templateUrl: './insee-card.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PropertyInseeCardComponent {
  insee = input<PropertyGeospatialInseeDto | null | undefined>();
  cityName = input<string | null | undefined>();
  expanded = model<boolean>(false);

  affluenceStepIndex = computed(() => {
    const level = this.insee()?.affluenceLevel;
    switch (level) {
      case 'modest':
        return 0;
      case 'median':
        return 1;
      case 'affluent':
        return 2;
      case 'very_affluent':
        return 3;
      default:
        return -1;
    }
  });

  affluenceInfo = computed(() => {
    const level = this.insee()?.affluenceLevel;
    switch (level) {
      case 'very_affluent':
        return {
          label: 'Quartier très aisé',
          classes: 'bg-purple-a2 text-purple-11 border-purple-a4',
          badgeBg: 'bg-purple-9',
          description: 'Revenus parmi les 10 % les plus élevés à l’échelle nationale.',
        };
      case 'affluent':
        return {
          label: 'Quartier aisé',
          classes: 'bg-jade-a2 text-jade-11 border-jade-a4',
          badgeBg: 'bg-jade-9',
          description: 'Revenus supérieurs à la moyenne de référence du territoire.',
        };
      case 'median':
        return {
          label: 'Quartier médian',
          classes: 'bg-indigo-a2 text-indigo-11 border-indigo-a4',
          badgeBg: 'bg-indigo-9',
          description: 'Revenus représentatifs du standard médian national.',
        };
      case 'modest':
        return {
          label: 'Quartier modeste',
          classes: 'bg-blue-a2 text-blue-11 border-blue-a4',
          badgeBg: 'bg-blue-9',
          description: 'Revenus inférieurs à la médiane départementale.',
        };
      default:
        return null;
    }
  });

  cityComparison = computed(() => {
    const percent = this.insee()?.comparisonToCityPercent;
    if (percent === undefined || percent === null) return null;
    return {
      percent,
      isPositive: percent > 0,
      isNegative: percent < 0,
      icon: percent > 0 ? 'trending_up' : percent < 0 ? 'trending_down' : 'trending_flat',
      colorClass: percent > 0 ? 'text-jade-11' : percent < 0 ? 'text-amber-11' : 'text-gray-11',
      bgClass:
        percent > 0
          ? 'bg-jade-a2 border-jade-a4'
          : percent < 0
            ? 'bg-amber-a2 border-amber-a4'
            : 'bg-gray-3 border-gray-6',
    };
  });

  departmentComparison = computed(() => {
    const percent = this.insee()?.comparisonToDepartmentPercent;
    if (percent === undefined || percent === null) return null;
    return {
      percent,
      isPositive: percent > 0,
      isNegative: percent < 0,
      icon: percent > 0 ? 'trending_up' : percent < 0 ? 'trending_down' : 'trending_flat',
      colorClass: percent > 0 ? 'text-jade-11' : percent < 0 ? 'text-amber-11' : 'text-gray-11',
      bgClass:
        percent > 0
          ? 'bg-jade-a2 border-jade-a4'
          : percent < 0
            ? 'bg-amber-a2 border-amber-a4'
            : 'bg-gray-3 border-gray-6',
    };
  });
}
